import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FeeManagementView from "./FeeManagement";
import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api";

function CampusFeePage({ onLogout, showToast }) {
  const { campusId } = useParams();
  const navigate = useNavigate();

  const [campus, setCampus] = useState(null);
  const [campusStudents, setCampusStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // The parent recreates onLogout/showToast on every render, so keeping them in
  // refs stops the load effect from re-running and re-mounting the fee view.
  const onLogoutRef = useRef(onLogout);
  const showToastRef = useRef(showToast);
  onLogoutRef.current = onLogout;
  showToastRef.current = showToast;

  useEffect(() => {
    if (!campusId) {
      navigate("/fees");
      return;
    }

    let cancelled = false;

    const loadCampusData = async () => {
      setLoading(true);
      try {
        // Fetch students scoped to this campus directly, instead of relying
        // on DashboardLayout's global student list (which can be stale if
        // students were added/edited without a full app reload).
        const [campusesRes, studentsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/campuses`, { headers: getJsonAuthHeaders() }),
          fetch(`${API_BASE_URL}/api/students?campus_id=${campusId}`, { headers: getJsonAuthHeaders() }),
        ]);

        if (campusesRes.status === 401 || studentsRes.status === 401) {
          onLogoutRef.current?.();
          return;
        }
        if (!campusesRes.ok) throw new Error("Failed to load campuses");
        if (!studentsRes.ok) throw new Error("Failed to load students");

        const campusesData = await campusesRes.json();
        const studentsData = await studentsRes.json();
        if (cancelled) return;

        const found = Array.isArray(campusesData)
          ? campusesData.find((c) => String(c.id) === String(campusId))
          : null;
        setCampus(found || null);
        setCampusStudents(Array.isArray(studentsData) ? studentsData : []);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load campus fee data", error);
        showToastRef.current?.("error", "Load Error", "Could not load campus details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCampusData();
    return () => { cancelled = true; };
  }, [campusId, navigate]);

  if (loading) {
    return (
      <div style={{ paddingTop: 20 }}>
        <div
          style={{
            margin: "24px",
            minHeight: 320,
            borderRadius: 16,
            border: "1px solid #dbe7f5",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#64748b",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Loading {campus?.name ? `${campus.name} ` : ""}fee management...
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 20 }}>
      <FeeManagementView
        students={campusStudents}
        campusId={campusId}
        onExit={() => navigate("/fees")}
      />
    </div>
  );
}

export default CampusFeePage;